/**
 * The AI layer picker's pure half — which rows exist, and which one is running.
 *
 * Only one AI layer runs at a time: `marketplace_runtime` keeps a single
 * process-global `_active_slug`, and road segmentation shares the same inference
 * budget. So this is a radio group, and the choice is server-wide rather than
 * scoped to one viewer.
 *
 * The geographic privacy mask is deliberately NOT in here. It is a projection of
 * OSM polygons through the camera pose, not an inference model, and it composes
 * with everything else — the menu carries it as a separate checkbox.
 */

import type { MarketplaceModel } from '../marketplace/marketplaceTypes';
import {
  channelFor, isServerReadable, PRIMARY, ROAD_SEG as CHANNEL_ROAD_SEG,
  type ServerChannel, type FeedSource,
} from './aiChannels';

/** Road segmentation is driven by /api/drone/road-seg, not by the marketplace. */
export const ROAD_SEG = 'road-segmentation';

/**
 * Generic YOLO detection with no marketplace model behind it — what the old
 * DETECT button did. Only offered when the catalogue is unreachable; see
 * `buildLayers`.
 */
export const PLAIN_DETECT = 'plain-detect';

export const OFF = 'off';

/**
 * Models that run as the camera's own CHANNEL even on the main feed: they read a
 * native-resolution sampler, and the primary pipeline only sees 640x360. The AI
 * LAYER popover assigns these through the channel API instead of the marketplace
 * activate route, which refuses them. Server twin: `ai_channels.CAMERA_ONLY_MODELS`.
 */
export const CAMERA_ONLY_MODELS: ReadonlySet<string> = new Set(['anpr']);

/**
 * What a feed's picture shows to anyone outside the backend. Server twin:
 * `feed_privacy.py`. `picture` is the full video; `blur` blurs heads in every
 * frame that leaves; `data_only` sends no picture at all — avatars, counts and
 * lines only.
 */
export type PrivacyMode = 'picture' | 'blur' | 'data_only';

/** One feed's privacy mode as GET /api/drone/overlays echoes it — the same
 *  feed-target shape `channels[]` carries, so it matches the same way. */
export interface PrivacyEntry {
  stream_key: string;
  mode: PrivacyMode;
  /** Only for bare keys like `demo`; a URL-keyed entry omits it, because the
   *  client may type an `rtsp://` camera `rtmp` and the server cannot know. */
  source_type?: string;
  source_url?: string | null;
  device_sn?: string | null;
}

export type LayerId = typeof OFF | typeof ROAD_SEG | typeof PLAIN_DETECT | string;

export interface LayerRow {
  id: LayerId;
  name: string;
  /** Longer text for the row's tooltip. */
  description: string | null;
  /** False when the row cannot be selected, with `reason` saying why. */
  selectable: boolean;
  /** Short badge shown on an unselectable row. */
  reason: string | null;
  /** Segmentation rows carry a mode sub-row; detection rows do not. */
  kind: 'off' | 'segmentation' | 'detection';
}

/** What GET /api/drone/overlays answers. */
export interface OverlayState {
  detection_enabled: boolean;
  model_slug: string | null;
  road_seg_enabled: boolean;
  road_seg_mode: 'blur' | 'overlay';
  geo_mask_enabled: boolean;
  gpu_combined: boolean;
  /**
   * The per-feed channels, so this picker can answer for the feed being
   * watched rather than only for the process-global primary pipeline.
   *
   * Optional because a node whose backend predates the field still answers the
   * rest, and the popover must keep working there — it simply falls back to
   * the globals, which is exactly what it did before.
   */
  channels?: ServerChannel[];
  /**
   * This deployment set `DJI_LOW_LATENCY`: a DJI tile should leave its HLS
   * player and take the server-composed picture, which is ~4-8 s closer to live.
   *
   * Deployment policy rather than pipeline state, but it arrives here because
   * this is the reading the dashboard already takes so it never has to assume
   * what the video pipeline is doing — and a tile genuinely cannot derive it:
   * nothing about an `.m3u8` says whether this box would rather serve the same
   * aircraft from its own reader.
   *
   * Optional for the same reason `channels` is: a backend predating the field
   * answers everything else, and those deployments must keep their HLS tile.
   */
  dji_low_latency?: boolean;
  /**
   * Another group armed the main processing feed and owns its AI pipeline: the
   * layer flags above read as off for this viewer, and switching them 403s.
   */
  primary_owned_elsewhere?: boolean;
  /**
   * Feeds whose privacy mode is not `picture`. A feed with no entry shows the
   * full picture. Optional because an older backend does not send it, and the
   * answer there is the picture it always sent.
   */
  privacy?: PrivacyEntry[];
}

const OFF_ROW: LayerRow = {
  id: OFF,
  name: 'Off',
  description: 'No AI layer on the feed',
  selectable: true,
  reason: null,
  kind: 'off',
};

const ROAD_SEG_ROW: LayerRow = {
  id: ROAD_SEG,
  name: 'Road Segmentation',
  description: 'Masks drivable road surface — blur it for privacy, or overlay it',
  selectable: true,
  reason: null,
  kind: 'segmentation',
};

const PLAIN_DETECT_ROW: LayerRow = {
  id: PLAIN_DETECT,
  name: 'Object Detection',
  description: 'Default YOLO weights on this node',
  selectable: true,
  reason: null,
  kind: 'detection',
};

/**
 * Assemble the rows for a catalogue response.
 *
 * `models` is /api/marketplace/my-models — free plus actively licensed. Pass
 * null when the request failed, which is not exotic: field nodes and the Jetsons
 * frequently never ran the migrations that create `marketplace_models`. Those
 * nodes fall back to a plain detection row wired to /api/drone/detection, so
 * they keep the detection the old DETECT button gave them.
 *
 * Rows that exist but cannot run are kept and disabled rather than filtered out.
 * An operator who cannot see the locked model cannot ask to buy it, and that
 * purchase flow is what the marketplace demo rests on.
 */
export function buildLayers(models: MarketplaceModel[] | null): LayerRow[] {
  const rows: LayerRow[] = [OFF_ROW, ROAD_SEG_ROW];

  if (models === null || models.length === 0) {
    rows.push(PLAIN_DETECT_ROW);
    return rows;
  }

  for (const m of models) {
    // Road seg has a catalogue entry but no MODEL_RUNTIMES entry, so activating
    // it through the marketplace 503s. The fixed row above drives it instead.
    if (m.slug === ROAD_SEG) continue;
    rows.push({
      id: m.slug,
      name: m.name,
      description: m.description,
      selectable: m.runnable,
      reason: m.runnable ? null : 'not installed',
      kind: m.category === 'segmentation' ? 'segmentation' : 'detection',
    });
  }

  // A catalogue that lists nothing runnable is no better than no catalogue.
  if (!rows.some(r => r.kind === 'detection' && r.selectable)) {
    rows.push(PLAIN_DETECT_ROW);
  }
  return rows;
}

/**
 * The channel standing on this feed that the popover must obey — or null.
 *
 * A channel and the primary pipeline are two different things that both draw on
 * one feed, and until 2026-09-01 this control could see only the second. A
 * `people-counting` channel persisted onto a VIGI camera restarted itself at
 * boot, so the operator selected that camera, watched it draw boxes, and read
 * `Off` on the button. Off was then inert twice over: the radio already said
 * Off so the click short-circuited, and the deactivate a mismatched click did
 * send stood down the primary detector, which was never what was drawing.
 *
 * The `primary` mirror is deliberately excluded. It is derived state restating
 * the globals below, and the primary path already owns those writes — obeying
 * it here would be a second, divergent route to one answer.
 */
export function feedChannel(state: OverlayState | null,
                            feed?: FeedSource | null): ServerChannel | null {
  if (!state || !feed || !state.channels) return null;
  const channel = channelFor(feed, state.channels);
  if (!channel || channel.name === PRIMARY) return null;
  return channel;
}

/**
 * Which row the radio should show as selected, given what the server reports.
 *
 * A channel on `feed` wins, because it is what is actually painting that
 * picture — see `feedChannel`. Otherwise the process-global primary state
 * answers, which is every feed that carries no channel of its own.
 *
 * Road seg wins over detection when both globals are somehow on. That is
 * reachable on a combined-inference server, where enabling road seg does not
 * clear detection — the menu's own writes turn the old layer off explicitly,
 * but a state set before this dashboard connected (or by another operator) can
 * arrive that way. A *channel* carrying both resolves the other way: the model
 * is the more informative of the two, matching `buildChannelRows`.
 */
export function activeLayer(state: OverlayState | null,
                            feed?: FeedSource | null): LayerId {
  if (!state) return OFF;
  const channel = feedChannel(state, feed);
  if (channel) {
    if (channel.model_slug) return channel.model_slug;
    return channel.road_seg ? ROAD_SEG : OFF;
  }
  if (state.road_seg_enabled) return ROAD_SEG;
  if (!state.detection_enabled) return OFF;
  return state.model_slug ?? PLAIN_DETECT;
}

/**
 * This picker's LayerId as the `<select>` value the channel API takes.
 *
 * One mapping, so a clear cannot be spelled differently by the two controls
 * that can both issue one — this popover and the Matrix sidebar, which posts
 * the same body through `assignBody`.
 */
export function channelValue(layer: LayerId): string {
  if (layer === OFF) return '';
  if (layer === ROAD_SEG) return CHANNEL_ROAD_SEG;
  // The plain DETECT row carries no marketplace slug. `object-detection-coco`
  // is the runtime it actually drives, and the name /api/drone/overlays already
  // reports for a primary running detection with no model selected.
  if (layer === PLAIN_DETECT) return 'object-detection-coco';
  return layer;
}

/** Label for the header button: the running layer's own name, in its own case. */
export function layerLabel(rows: LayerRow[], active: LayerId): string {
  if (active === OFF) return 'AI layer';
  const row = rows.find(r => r.id === active);
  // An unknown slug is a code (anpr, ppe-detection) — upper-case reads as one.
  return row ? row.name : active.toUpperCase();
}

/**
 * The privacy mode standing on this feed, matched exactly the way `feedChannel`
 * matches a channel — on the aircraft's serial, else on the URL posted for it.
 * No entry means the full picture.
 */
export function feedPrivacy(state: OverlayState | null,
                            feed?: FeedSource | null): PrivacyMode {
  if (!state || !feed || !state.privacy) return 'picture';
  return channelFor(feed, state.privacy)?.mode ?? 'picture';
}

const PRIVACY_LABELS: Record<PrivacyMode, string> = {
  picture: 'Picture',
  blur: 'Blur',
  data_only: 'Data only',
};

export function privacyLabel(mode: PrivacyMode): string {
  return PRIVACY_LABELS[mode];
}

const PRIVACY_RANK: Record<PrivacyMode, number> = { data_only: 0, blur: 1, picture: 2 };

/** True when `to` shows viewers more than `from` does — the direction that asks first. */
export function isLoosening(from: PrivacyMode, to: PrivacyMode): boolean {
  return PRIVACY_RANK[to] > PRIVACY_RANK[from];
}

/**
 * Why this feed cannot take a privacy mode, or null when it can.
 *
 * A mode only changes a picture the SERVER composes. An aircraft is the trap:
 * it can carry a model (`canCarryModel` names it by serial), but its tile keeps
 * playing the raw HLS in the browser whatever mode is set — offering one would
 * claim faces are hidden while the tile shows them. So no serial exception here.
 */
export function privacyUnavailableReason(feed?: FeedSource | null): string | null {
  if (!feed) return 'No feed selected';
  if (feed.deviceSn) {
    return 'Aircraft video is decoded in the browser — privacy modes apply to server-read feeds only';
  }
  if (!isServerReadable(feed.type)) {
    return 'This feed is decoded in the browser — the server cannot change its picture';
  }
  return null;
}
